/**
 * Video Upload Service - Firebase Storage SDK Implementation
 * Uses Firebase Storage SDK with metadata for server-side processing
 */

import { useAuthStore } from '@/stores/authStore';
// Note: Using 'storage' (default bucket) for video uploads instead of 'userStorage' (custom bucket)
// This is intentional as videos are processed differently than user profile data like avatars
// Videos require server-side processing and may have different retention/access policies
import { ref, uploadBytesResumable, updateMetadata, StorageReference, getDownloadURL } from 'firebase/storage';
import fixWebmDurationLib from 'fix-webm-duration';
import { config } from '@/config';
import { storage } from '@/firebase/firebase';
import { errorReporter } from '@/services/errorReportingService';

export interface UploadProgress {
  phase: 'preparing' | 'uploading' | 'metadata' | 'complete';
  progress: number; // 0-100
  message: string;
}

export interface VideoUploadMetadata {
  trim_enabled: string;
  crop_enabled?: string;
  crop_x?: string;
  crop_y?: string;
  crop_width?: string;
  crop_height?: string;
  audio_remove?: string;
  processing_type: string;
}

export interface UploadResult {
  success: boolean;
  noteId?: string;
  filePath?: string;
  error?: string;
}

export class VideoUploadService {
  /**
   * Upload video file to Firebase Storage and create a note
   * @param videoFile - Video file to upload
   * @param onProgress - Progress callback
   * @returns Promise<UploadResult>
   */
  async uploadVideo(
    videoFile: File,
    onProgress?: (progress: UploadProgress) => void,
    durationSeconds?: number,
  ): Promise<UploadResult> {
    try {
      onProgress?.({
        phase: 'preparing',
        progress: 3,
        message: 'Preparing upload...',
      });

      // Get current user and fresh Firebase auth token
      const { currentUser, userData } = useAuthStore.getState();
      if (!currentUser || !userData) {
        throw new Error('User not authenticated or user data not loaded');
      }

      onProgress?.({
        phase: 'preparing',
        progress: 5,
        message: 'Getting fresh authentication token...',
      });

      // Get a fresh Firebase ID token (force refresh to ensure it's valid)
      const freshToken = await currentUser.getIdToken(true);
      if (!freshToken) {
        throw new Error('Failed to get authentication token');
      }

      // Use the user document ID from backend, not Firebase UID
      const creatorUserId = userData.id;

      onProgress?.({
        phase: 'preparing',
        progress: 7,
        message: 'Creating note in database...',
      });

      // Create note in database FIRST to get the real note ID
      const noteId = await this.createNote(freshToken);

      // Create file path using the REAL note ID from database
      const filePath = `${creatorUserId}/${noteId}/file.webm`;

      onProgress?.({
        phase: 'preparing',
        progress: 10,
        message: 'Processing video file...',
      });

      // Fix WebM duration metadata when the recorder provided the elapsed duration
      const fixedVideoBlob = await this.fixWebmDuration(videoFile, durationSeconds);

      onProgress?.({
        phase: 'uploading',
        progress: 12,
        message: 'Uploading video to Firebase Storage...',
      });

      // Upload to Firebase Storage using SDK with REAL note ID
      const storageRef = ref(storage, filePath);

      await new Promise<void>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, fixedVideoBlob, {
          contentType: 'video/webm',
          customMetadata: {
            uploadedBy: 'web_app',
            originalSize: fixedVideoBlob.size.toString(),
          },
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const firebaseProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            // Scale Firebase's 0-100% into our 12-90% range
            const scaledProgress = Math.round(12 + firebaseProgress * 0.78);
            onProgress?.({
              phase: 'uploading',
              progress: scaledProgress,
              message: `Uploading video... ${Math.round(firebaseProgress)}%`,
            });
          },
          (error) => reject(error),
          () => resolve(),
        );
      });

      onProgress?.({
        phase: 'metadata',
        progress: 92,
        message: 'Setting processing metadata...',
      });

      // Set metadata for server-side processing (Firebase Storage SDK approach)
      await this.setVideoMetadata(storageRef);

      onProgress?.({
        phase: 'metadata',
        progress: 96,
        message: 'Linking video to note...',
      });

      // Get download URL for the uploaded video
      const downloadURL = await getDownloadURL(storageRef);

      // Update note with video information (same as Chrome extension)
      await this.updateNoteWithVideo(noteId, downloadURL, freshToken);

      onProgress?.({
        phase: 'complete',
        progress: 100,
        message: 'Upload complete!',
      });

      return {
        success: true,
        noteId,
        filePath,
      };
    } catch (error) {
      errorReporter.critical('Video upload failed', error, {
        feature: 'upload',
        operation: 'uploadVideo',
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  /**
   * Fix WebM duration metadata using the fix-webm-duration library.
   * MediaRecorder-produced WebM files lack a duration header; this appends it.
   * Skips non-WebM blobs (e.g. Safari mp4) and falls back to the original blob
   * on any failure — the upload must never break because of metadata fixing.
   */
  private async fixWebmDuration(videoBlob: Blob, durationSeconds?: number): Promise<Blob> {
    if (!durationSeconds || durationSeconds <= 0) {
      return videoBlob;
    }

    if (!videoBlob.type.includes('webm')) {
      return videoBlob;
    }

    try {
      const durationMs = Math.round(durationSeconds * 1000);
      const fixedBlob = await fixWebmDurationLib(videoBlob, durationMs, { logger: false });
      return fixedBlob ?? videoBlob;
    } catch (error) {
      errorReporter.expected('Failed to fix WebM duration metadata, uploading original blob', error, {
        feature: 'upload',
        operation: 'fixWebmDuration',
      });
      return videoBlob;
    }
  }

  /**
   * Set video processing metadata using Firebase Storage SDK
   */
  private async setVideoMetadata(storageRef: StorageReference): Promise<void> {
    const processingMetadata = {
      trim_enabled: 'false',
      crop_enabled: 'false',
      audio_remove: 'false',
      processing_type: 'none',
      uploaded_from: 'web_app',
      upload_timestamp: new Date().toISOString(),
    };

    // Update metadata using Firebase Storage SDK
    await updateMetadata(storageRef, {
      customMetadata: processingMetadata,
    });
  }

  /**
   * Create note in database (same as Chrome extension)
   */
  private async createNote(token: string): Promise<string> {
    const response = await fetch(`${config.apiUrl}/notes/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        source: 'website',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Failed to create note (${response.status}): ${errorText || response.statusText}`);
    }

    const noteData = await response.json();
    return noteData.id; // Return the created note ID
  }

  /**
   * Update note with video information (same as Chrome extension)
   */
  private async updateNoteWithVideo(noteId: string, downloadURL: string, token: string): Promise<void> {
    const updateData = {
      video_url: downloadURL,
      processing_params: {
        trim_enabled: false,
        crop_enabled: false,
        remove_audio: false,
      },
      trim_enabled: false,
      crop_enabled: false,
      remove_audio: false,
    };

    const response = await fetch(`${config.apiUrl}/notes/${noteId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Failed to update note with video: ${response.status} ${errorText}`);
    }
  }
}
